/**
 * View a consultation form's photo — the WhatsApp picture intake (2026-10-14).
 *
 * A form photographed on WhatsApp is kept in the PRIVATE `picture-intakes`
 * bucket: nothing serves it publicly, because it carries a member's health
 * answers. The only way to see one is `GET /api/members/picture-intakes/:id/photo`,
 * which signs a URL for five minutes — so this asks for a FRESH one each time
 * the photo is opened, never caches it, and never puts it anywhere but the
 * `<img>` (and the "Open full size" link, for reading small handwriting).
 * The endpoint sits under `/api/members/`, so only staff the Members page is
 * granted to get one.
 *
 * Used by the member page's "Consultation form" modal and the Members page's
 * "Forms waiting for a member".
 */

import { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, ApiError } from "../lib/api";

export function PictureIntakePhotoButton({
  intakeId,
  label = "View photo",
}: {
  readonly intakeId: string;
  readonly label?: string;
}): JSX.Element {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" className="pi-link-btn" onClick={() => setOpen(true)}>
        {label}
      </button>
      {open ? (
        <PictureIntakePhotoModal intakeId={intakeId} onClose={() => setOpen(false)} />
      ) : null}
    </>
  );
}

function photoErrorText(e: unknown): string {
  if (e instanceof ApiError) {
    if (e.code === "picture_intake_photo_not_found") return "This photo has been deleted.";
    if (e.code === "picture_intake_storage_unbound" || e.code === "picture_intakes_unbound") {
      return "Photos can't be shown on this server — its storage isn't connected.";
    }
  }
  return `Couldn't load the photo: ${(e as Error).message}`;
}

function PictureIntakePhotoModal({
  intakeId,
  onClose,
}: {
  readonly intakeId: string;
  readonly onClose: () => void;
}): JSX.Element {
  const [url, setUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const r = await api.pictureIntakePhoto(intakeId);
        if (!cancelled) setUrl(r.url);
      } catch (e) {
        if (!cancelled) setError(photoErrorText(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [intakeId]);

  const onBackdrop = useCallback(
    (e: React.MouseEvent): void => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose],
  );

  return createPortal(
    <div className="modal-backdrop" role="presentation" onMouseDown={onBackdrop}>
      <div
        className="modal-card pi-photo-card"
        role="dialog"
        aria-modal="true"
        aria-label="Consultation form photo"
      >
        <h3 className="modal-title">Consultation form photo</h3>
        {url === null && error === null ? <p className="dc-muted">Loading the photo…</p> : null}
        {error !== null ? <p className="action-modal-error">{error}</p> : null}
        {url !== null ? (
          <img
            className="pi-photo-img"
            src={url}
            alt="The consultation form, as photographed on WhatsApp"
            onError={() => setError("The photo link expired or couldn't be read. Close and open it again.")}
          />
        ) : null}
        <div className="modal-actions modal-actions-split">
          <button type="button" className="row-edit-modal-cancel" onClick={onClose}>
            Close
          </button>
          {url !== null ? (
            <a className="pi-link-btn" href={url} target="_blank" rel="noreferrer noopener">
              Open full size
            </a>
          ) : null}
        </div>
      </div>
    </div>,
    document.body,
  );
}
